/**
 * Data caching utility for venue and event information
 * Uses sessionStorage for temporary caching during user session
 */

const CACHE_PREFIX = 'bc_cache_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Generate cache key with prefix
 */
const getCacheKey = (type, id) => {
  return `${CACHE_PREFIX}${type}_${id}`;
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (cachedItem) => {
  if (!cachedItem || !cachedItem.timestamp) {
    return false;
  }
  const now = Date.now();
  return (now - cachedItem.timestamp) < CACHE_DURATION;
};

/**
 * Get data from cache
 * @param {string} type - Type of data (e.g., 'venue', 'event')
 * @param {string|number} id - Unique identifier
 * @returns {object|null} Cached data or null if not found/expired
 */
export const getCachedData = (type, id) => {
  try {
    const cacheKey = getCacheKey(type, id);
    const cachedString = sessionStorage.getItem(cacheKey);

    if (!cachedString) {
      return null;
    }

    const cachedItem = JSON.parse(cachedString);

    if (!isCacheValid(cachedItem)) {
      // Cache expired, remove it
      sessionStorage.removeItem(cacheKey);
      return null;
    }

    return cachedItem.data;
  } catch (error) {
    console.error('Error reading from cache:', error);
    return null;
  }
};

/**
 * Set data in cache
 * @param {string} type - Type of data (e.g., 'venue', 'event')
 * @param {string|number} id - Unique identifier
 * @param {object} data - Data to cache
 */
export const setCachedData = (type, id, data) => {
  try {
    const cacheKey = getCacheKey(type, id);
    const cacheItem = {
      data,
      timestamp: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheItem));
  } catch (error) {
    console.error('Error writing to cache:', error);
  }
};

/**
 * Clear specific cached item
 * @param {string} type - Type of data
 * @param {string|number} id - Unique identifier
 */
export const clearCachedData = (type, id) => {
  try {
    const cacheKey = getCacheKey(type, id);
    sessionStorage.removeItem(cacheKey);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Clear all cached items with the prefix
 */
export const clearAllCache = () => {
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing all cache:', error);
  }
};
